// Header button that opens the Zonos Hello duties estimate. Zonos starts only after the whole page has loaded,
// so a click can come first. Then the button is marked busy, and the dialog opens when Zonos is ready.
// Also: the header wishlist link without preference consent (see "Wishlist without preference consent").

// One function scope, so these names cannot clash with the many app scripts on the page.
(function () {
  if (window.svahaHeaderWidgets) {
    return;
  }
  window.svahaHeaderWidgets = true;

  const WIDGET_WAIT_MS = 60000;

  // aria-busy tells screen readers the button is waiting. theme.css also uses it to show a pulse.
  function setBusy(button, busy) {
    if (!button) {
      return;
    }
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  // Runs action now if the widget is ready. If not, marks the button busy, checks every 200 ms, and runs action
  // once the widget is ready. Several clicks while a widget loads still run action once.
  const waiting = {};

  function cancelWaiting(name) {
    const entry = waiting[name];
    if (!entry) {
      return;
    }
    clearInterval(entry.timer);
    setBusy(entry.button, false);
    waiting[name] = null;
  }

  function whenReady(name, button, isReady, action) {
    if (isReady()) {
      action();
      return;
    }
    if (waiting[name]) {
      return;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      const ready = isReady();
      if (ready || Date.now() - started > WIDGET_WAIT_MS) {
        cancelWaiting(name);
        if (ready) {
          action();
        }
      }
    }, 200);
    waiting[name] = { button, timer };
    setBusy(button, true);
  }

  // Zonos. Zonos.openHelloDialog() exists before the <zonos-hello> element has built its dialog, and a call in that
  // gap does nothing. So "ready" also needs the dialog inside the element.

  function zonosHello() {
    return document.querySelector('zonos-hello');
  }

  function zonosReady() {
    const hello = zonosHello();
    return Boolean(
      window.Zonos &&
      typeof window.Zonos.openHelloDialog === 'function' &&
      hello &&
      hello.shadowRoot &&
      hello.shadowRoot.querySelector('zonos-hello-dialog'),
    );
  }

  function zonosDialogOpen() {
    const dialog = zonosHello()?.shadowRoot?.querySelector('zonos-hello-dialog');
    const panel = dialog?.shadowRoot?.querySelector('.zonos--hello-dialog');
    return Boolean(panel && panel.getBoundingClientRect().height > 0);
  }

  // Each open gets a number. A close, or a newer open, changes the number, so older retries stop.
  let zonosOpenRun = 0;

  // openHelloDialog(false) closes the dialog. A second openHelloDialog() does not close it.
  function closeZonos() {
    zonosOpenRun += 1;
    if (zonosReady() && zonosDialogOpen()) {
      window.Zonos.openHelloDialog(false);
    }
  }

  // A click on the flag while the dialog is open closes it. Without this, the retries below can open it again.
  // The first call right after the dialog appears can still be missed. Call again a few times until it shows.
  // Stop once it has shown, so a close by the visitor is not undone.
  function openZonosDialog() {
    if (zonosDialogOpen()) {
      closeZonos();
      return;
    }
    zonosOpenRun += 1;
    const run = zonosOpenRun;
    let tries = 0;
    const attempt = () => {
      window.Zonos.openHelloDialog();
      tries += 1;
      if (tries < 5) {
        setTimeout(() => {
          if (run === zonosOpenRun && !zonosDialogOpen()) {
            attempt();
          }
        }, 300);
      }
    };
    attempt();
  }

  function openZonos(button) {
    whenReady('zonos', button, zonosReady, () => {
      // On phones the country selector is in the menu drawer. Close the drawer first, so the Zonos sheet is not
      // behind it. Not before Zonos is ready: the drawer holds the busy button, so the visitor sees the wait.
      const sidebar = document.getElementById('sidebar-menu');
      if (sidebar && sidebar.open) {
        sidebar.hide();
      }
      openZonosDialog();
    });
  }

  // The header replaces the Zonos floating flag only where it renders a Zonos button. In the US the header has no
  // Zonos button on purpose: there are no duties to estimate. Elsewhere the button needs the country flag setting.
  // Without a header button, keep the floating flag, so the duties estimate stays reachable.
  function headerHasZonos() {
    return Boolean(document.querySelector('[data-open-zonos]')) || window.Shopify?.country === 'US';
  }

  // The flag and the Zonos dialog are in the same shadow root, and the dialog takes its position from the flag.
  // So the flag is made invisible, not removed. With the flag removed, the desktop dialog opens off screen.
  function hideZonosFlag() {
    const hello = zonosHello();
    if (!hello || !hello.shadowRoot) {
      return false;
    }
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync('.zonos--hello-widget { visibility: hidden !important; pointer-events: none !important; }');
      hello.shadowRoot.adoptedStyleSheets = [...hello.shadowRoot.adoptedStyleSheets, sheet];
    } catch (error) {
      // An old browser without constructable stylesheets. The flag cannot be hidden alone. Show the whole widget,
      // so the dialog can open. A visible flag is better than a dialog nobody can see.
    }
    showZonosWidget();
    return true;
  }

  // theme.css hides the whole Zonos widget, dialog included, until this class is on <html>.
  function showZonosWidget() {
    document.documentElement.classList.add('zonos-flag-hidden');
  }

  // The Zonos element gets its shadow root when it hydrates, and it adds the class "hydrated" then.
  // So watch attributes too, not only new elements. Stop watching after 60 seconds. If Zonos is still not there,
  // show its widget anyway. Zonos may load later, and then it shows its own flag, but its dialog is visible.
  if (!headerHasZonos()) {
    showZonosWidget();
  } else if (!hideZonosFlag()) {
    const observer = new MutationObserver(() => {
      if (hideZonosFlag()) {
        observer.disconnect();
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    setTimeout(() => {
      observer.disconnect();
      showZonosWidget();
    }, WIDGET_WAIT_MS);
  }

  // Wishlist without preference consent. The wishlist (wishlist.js) works only with preference consent, so
  // until then the header link has nothing to show. It is marked unavailable (aria-disabled, dimmed in
  // header-widgets.css), and a click opens a note that explains why and opens Shopify's cookie preferences.

  const wishlistLink = document.querySelector('[data-wishlist-link]');
  const consentNote = document.getElementById('wishlist-consent-note');

  function wishlistBlocked() {
    return wishlistLink?.getAttribute('aria-disabled') === 'true';
  }

  function updateWishlistLink() {
    const blocked = window.Shopify.customerPrivacy.preferencesProcessingAllowed() === false;
    if (blocked) {
      wishlistLink.setAttribute('aria-disabled', 'true');
      wishlistLink.setAttribute('aria-describedby', 'wishlist-consent-note-text');
    } else {
      wishlistLink.removeAttribute('aria-disabled');
      wishlistLink.removeAttribute('aria-describedby');
      closeConsentNote();
    }
  }

  function closeConsentNote() {
    if (consentNote.matches(':popover-open')) {
      consentNote.hidePopover();
    }
  }

  // The note is in the top layer, so it is placed against the window: under the link, inside the window edges.
  function openConsentNote() {
    const preferences = consentNote.querySelector('[data-wishlist-consent-preferences]');
    preferences.hidden = typeof window.privacyBanner?.showPreferences !== 'function';
    consentNote.showPopover();
    // Measure at the left edge. A fixed box with left set is only as wide as the space to its right, so a
    // width measured at the last position would shrink each time.
    consentNote.style.left = '0px';
    const link = wishlistLink.getBoundingClientRect();
    const width = consentNote.offsetWidth;
    const centre = link.left + link.width / 2;
    const left = Math.max(8, Math.min(centre - width / 2, window.innerWidth - width - 8));
    consentNote.style.top = `${link.bottom + 10}px`;
    consentNote.style.left = `${left}px`;
    consentNote.style.setProperty('--wishlist-consent-arrow', `${centre - left}px`);
    (preferences.hidden ? consentNote.querySelector('[data-wishlist-consent-close]') : preferences).focus();
  }

  // Browsers without the Popover API (Safari before 17) skip this, and the link stays as it was.
  if (wishlistLink && typeof consentNote?.showPopover === 'function' && window.svahaConsentReady) {
    // svahaConsentReady: see snippets/consent-gate.liquid.
    window.svahaConsentReady.then(() => {
      updateWishlistLink();
      document.addEventListener('visitorConsentCollected', updateWishlistLink);
    });

    // Shopify's preferences window takes focus, so the note must not return it to the link then.
    let openingPreferences = false;
    consentNote.querySelector('[data-wishlist-consent-close]').addEventListener('click', closeConsentNote);
    consentNote.querySelector('[data-wishlist-consent-preferences]').addEventListener('click', () => {
      openingPreferences = true;
      closeConsentNote();
      window.privacyBanner.showPreferences();
    });
    // Escape and a click elsewhere also close the note (popover). Return focus to the link, unless the visitor
    // moved it somewhere else.
    consentNote.addEventListener('toggle', (event) => {
      if (event.newState !== 'closed') {
        return;
      }
      if (!openingPreferences && (document.activeElement === document.body || !document.activeElement)) {
        wishlistLink.focus();
      }
      openingPreferences = false;
    });
    // Close the note when Tab moves focus past it, so it does not cover what gets focus next. Back to the link
    // keeps it open.
    consentNote.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next && next !== wishlistLink && !consentNote.contains(next)) {
        closeConsentNote();
      }
    });
    // The note is placed once. Close it when the page scrolls or the window changes size, so it does not float
    // away from the link.
    window.addEventListener('scroll', closeConsentNote, { passive: true });
    window.addEventListener('resize', closeConsentNote);
  }

  // Capture phase, so this runs before any other handler on the wishlist link.
  document.addEventListener(
    'click',
    (event) => {
      const zonosButton = event.target.closest('[data-open-zonos]');
      if (zonosButton) {
        openZonos(zonosButton);
        return;
      }

      // Stop the click here while the link is unavailable, so the browser does not open the empty page.
      if (wishlistBlocked() && wishlistLink.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        openConsentNote();
      }
    },
    true,
  );
})();
